import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Plus, Pencil, Ban, ArrowUpRight, ShieldCheck, Layers } from "lucide-react";
import { toast } from "sonner";
import BulkAssignDialog from "@/components/training/BulkAssignDialog";

const REQ_TYPES = [
  { v: "mandatory",     label: "Mandatory",       tone: "bg-red-100 text-red-800 border-red-200" },
  { v: "role_required", label: "Role Required",   tone: "bg-amber-100 text-amber-900 border-amber-300" },
  { v: "recommended",   label: "Recommended",     tone: "bg-sky-100 text-sky-800 border-sky-200" },
  { v: "optional",      label: "Optional",        tone: "bg-slate-100 text-slate-700 border-slate-200" },
];

const ROLES = ["care_worker", "manager", "deputy_manager", "service_manager", "registered_manager", "admin"];
const SERVICE_TYPES = ["supported_living", "residential_care", "domiciliary_care", "nursing_care"];

function reqTone(rt) { return (REQ_TYPES.find((r) => r.v === rt) || REQ_TYPES[3]).tone; }
function reqLabel(rt) { return (REQ_TYPES.find((r) => r.v === rt) || REQ_TYPES[3]).label; }

export default function CoursesTab({ canManage, onChanged, refreshTick }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [sites, setSites] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/training/courses", { params: { active_only: !showInactive } });
      setRows(r.data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [showInactive]);

  useEffect(() => { load(); }, [load, refreshTick]);
  useEffect(() => { api.get("/sites").then((r) => setSites(r.data || [])).catch(() => {}); }, []);

  const deactivate = async (row) => {
    if (!window.confirm(`Deactivate "${row.name}"? Existing training records remain intact.`)) return;
    try {
      await api.delete(`/training/courses/${row.id}`);
      toast.success("Course deactivated");
      load(); onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <section className="bg-white border border-slate-200 rounded-lg" data-testid="training-courses-tab">
      <div className="p-4 border-b border-slate-200 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-heading text-base font-semibold text-slate-900 flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-sky-700" /> Course Catalogue
          </h2>
          <p className="text-xs text-slate-500 mt-0.5 max-w-xl">Configure which courses are Mandatory, Role Required, Recommended or Optional. Assign courses by role, site or service type.</p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-slate-600 flex items-center gap-1.5">
            <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} data-testid="courses-show-inactive" /> Show deactivated
          </label>
          {canManage && (
            <>
              <Button variant="outline" size="sm" onClick={() => setBulkOpen(true)} data-testid="btn-course-bulk-assign" className="h-8 text-xs">
                <Layers className="w-3.5 h-3.5 mr-1.5" /> Bulk Assign
              </Button>
              <Button size="sm" onClick={() => setCreating(true)} data-testid="btn-course-new" className="h-8 bg-sky-700 hover:bg-sky-800">
                <Plus className="w-3.5 h-3.5 mr-1.5" /> New Course
              </Button>
            </>
          )}
        </div>
      </div>

      {loading ? (
        <div className="p-8 text-sm text-slate-500">Loading catalogue…</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="data-table" data-testid="training-courses-table">
            <thead>
              <tr>
                <th>Course</th>
                <th>Category</th>
                <th>Requirement</th>
                <th>Renewal</th>
                <th>Applies to</th>
                <th>Status</th>
                <th className="w-20 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.id} data-testid={`course-row-${c.id}`}>
                  <td>
                    <div className="font-medium text-slate-900">{c.name}</div>
                    {c.description && <div className="text-[11px] text-slate-500 max-w-md truncate">{c.description}</div>}
                  </td>
                  <td className="text-slate-500">{c.category || "—"}</td>
                  <td>
                    <span className={`inline-flex items-center text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-full border ${reqTone(c.requirement_type)}`}>{reqLabel(c.requirement_type)}</span>
                  </td>
                  <td className="tabular-nums text-slate-700">{c.renewal_months ? `${c.renewal_months} mo` : "—"}</td>
                  <td className="text-xs text-slate-600">
                    {(c.roles || []).length === 0 && (c.site_ids || []).length === 0 && (c.service_types || []).length === 0
                      ? "Everyone"
                      : [
                          (c.roles || []).length ? `${c.roles.length} role(s)` : null,
                          (c.site_ids || []).length ? `${c.site_ids.length} site(s)` : null,
                          (c.service_types || []).length ? `${c.service_types.length} service type(s)` : null,
                        ].filter(Boolean).join(" · ")}
                  </td>
                  <td>
                    {c.is_active ? <span className="text-[11px] text-emerald-700 font-medium">Active</span> : <span className="text-[11px] text-slate-500">Deactivated</span>}
                  </td>
                  <td className="text-right whitespace-nowrap">
                    {canManage && (
                      <>
                        <button onClick={() => setEditing(c)} className="p-1 text-slate-500 hover:text-sky-700" data-testid={`btn-course-edit-${c.id}`} title="Edit"><Pencil className="w-3.5 h-3.5" /></button>
                        {c.is_active && <button onClick={() => deactivate(c)} className="p-1 text-slate-500 hover:text-red-600 ml-0.5" data-testid={`btn-course-deactivate-${c.id}`} title="Deactivate"><Ban className="w-3.5 h-3.5" /></button>}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CourseDialog
        open={creating || !!editing}
        course={editing}
        onOpenChange={(v) => { if (!v) { setEditing(null); setCreating(false); } }}
        onSaved={() => { setEditing(null); setCreating(false); load(); onChanged?.(); }}
        sites={sites}
      />
      <BulkAssignDialog
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        onSaved={() => { setBulkOpen(false); load(); onChanged?.(); }}
      />
    </section>
  );
}

function CourseDialog({ open, course, onOpenChange, onSaved, sites }) {
  const [form, setForm] = useState(defaultCourse());
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) setForm(course ? { ...defaultCourse(), ...course } : defaultCourse());
  }, [open, course]);

  const patch = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const toggleList = (key, val) => {
    const cur = form[key] || [];
    patch(key, cur.includes(val) ? cur.filter((x) => x !== val) : [...cur, val]);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Course name required");
    setSaving(true);
    try {
      if (course?.id) await api.patch(`/training/courses/${course.id}`, form);
      else await api.post("/training/courses", form);
      toast.success(course?.id ? "Course updated" : "Course added");
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-white max-h-[90vh] overflow-y-auto" data-testid="course-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{course?.id ? "Edit Course" : "New Course"}</DialogTitle>
          <DialogDescription>Configure how this course applies to your staff.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <FLabel label="Course name *"><Input value={form.name} onChange={(e) => patch("name", e.target.value)} data-testid="course-input-name" /></FLabel>
          <FLabel label="Category"><Input value={form.category || ""} onChange={(e) => patch("category", e.target.value)} data-testid="course-input-category" placeholder="Clinical, Safeguarding, Values…" /></FLabel>
          <FLabel label="Requirement type *">
            <select value={form.requirement_type} onChange={(e) => patch("requirement_type", e.target.value)} data-testid="course-input-req" className="h-9 w-full text-sm border border-slate-300 rounded-md bg-white px-2">
              {REQ_TYPES.map((r) => <option key={r.v} value={r.v}>{r.label}</option>)}
            </select>
          </FLabel>
          <FLabel label="Renewal (months)">
            <Input type="number" min="0" max="120" value={form.renewal_months ?? ""} onChange={(e) => patch("renewal_months", e.target.value === "" ? null : Number(e.target.value))} data-testid="course-input-renewal" />
          </FLabel>
          <div className="col-span-2">
            <FLabel label="Description">
              <Textarea rows={2} value={form.description || ""} onChange={(e) => patch("description", e.target.value)} data-testid="course-input-desc" />
            </FLabel>
          </div>
          <div className="col-span-2">
            <FLabel label={form.requirement_type === "role_required" ? "Applies to roles *" : "Restrict to specific roles (optional)"}>
              <div className="flex flex-wrap gap-2 mt-1">
                {ROLES.map((r) => (
                  <label key={r} className="flex items-center gap-1 text-xs text-slate-700 capitalize">
                    <input type="checkbox" checked={(form.roles || []).includes(r)} onChange={() => toggleList("roles", r)} data-testid={`course-role-${r}`} />
                    {r.replace(/_/g, " ")}
                  </label>
                ))}
              </div>
              {form.requirement_type === "role_required" && (form.roles || []).length === 0 && (
                <div className="text-[11px] text-amber-700 mt-1">Role Required courses need at least one role selected.</div>
              )}
            </FLabel>
          </div>
          <div className="col-span-2">
            <FLabel label="Restrict to sites (optional)">
              <div className="flex flex-wrap gap-2 mt-1">
                {sites.map((s) => (
                  <label key={s.id} className="flex items-center gap-1 text-xs text-slate-700">
                    <input type="checkbox" checked={(form.site_ids || []).includes(s.id)} onChange={() => toggleList("site_ids", s.id)} data-testid={`course-site-${s.id}`} />
                    {s.name}
                  </label>
                ))}
                {sites.length === 0 && <span className="text-[11px] text-slate-500">No sites configured.</span>}
              </div>
            </FLabel>
          </div>
          <div className="col-span-2">
            <FLabel label="Restrict to service types (optional)">
              <div className="flex flex-wrap gap-2 mt-1">
                {SERVICE_TYPES.map((s) => (
                  <label key={s} className="flex items-center gap-1 text-xs text-slate-700 capitalize">
                    <input type="checkbox" checked={(form.service_types || []).includes(s)} onChange={() => toggleList("service_types", s)} data-testid={`course-service-${s}`} />
                    {s.replace(/_/g, " ")}
                  </label>
                ))}
              </div>
            </FLabel>
          </div>
          <div className="col-span-2 flex items-center gap-2 text-xs">
            <input type="checkbox" checked={!!form.is_active} onChange={(e) => patch("is_active", e.target.checked)} data-testid="course-input-active" />
            <span className="text-slate-700">Active — surface in compliance calculations</span>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-3 border-t border-slate-200 mt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="btn-course-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-sky-700 hover:bg-sky-800" data-testid="btn-course-save">{saving ? "Saving…" : (course?.id ? "Save changes" : "Add course")}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function defaultCourse() {
  return {
    name: "", category: "General", requirement_type: "optional",
    renewal_months: 12, description: "", is_active: true,
    roles: [], site_ids: [], service_types: [],
  };
}

function FLabel({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</span>
      <div className="mt-0.5">{children}</div>
    </label>
  );
}
